import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { ChevronDown, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { getToolConfig, humanizeToolId } from '../../../lib/tool-configs';
import { getRemoteToolConfig } from '../../../lib/tool-configs/remote';
export function isProvenance(v) {
    const p = v;
    return !!p && Array.isArray(p.tools) && typeof p.figures?.checked === 'number';
}
/** The figures no source accounts for, for marking in the answer's text. */ export function unmatchedFigures(p) {
    return (p?.figures.unmatched ?? []).map((f)=>f.text);
}
function toolName(id) {
    return getToolConfig(id)?.displayName ?? getRemoteToolConfig(id)?.displayName ?? humanizeToolId(id);
}
/**
 * "How I got this", under an answer: what the turn read and whether every
 * figure in the answer traces back to it. Quiet when everything checks out;
 * says plainly which figures don't.
 */ export function ProvenancePart({ provenance }) {
    const [open, setOpen] = useState(false);
    const { tools, figures } = provenance;
    const unmatched = figures.unmatched ?? [];
    const failed = tools.reduce((n, t)=>n + (t.failed ?? 0), 0);
    const reads = tools.reduce((n, t)=>n + t.calls, 0);
    const summary = [
        reads === 0 ? 'Answered from the conversation' : `Checked ${reads} source${reads === 1 ? '' : 's'}`,
        figures.checked > 0 ? unmatched.length === 0 ? `all ${figures.checked} figure${figures.checked === 1 ? '' : 's'} match a source` : `${unmatched.length} of ${figures.checked} figure${figures.checked === 1 ? '' : 's'} not found in a source` : ''
    ].filter(Boolean).join(' · ');
    const Icon = unmatched.length > 0 ? TriangleAlert : ShieldCheck;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-2 text-[length:var(--fs-ui-caption)] text-muted-foreground",
        "data-testid": Testids.assistant.provenance,
        children: [
            /*#__PURE__*/ _jsxs("button", {
                "aria-expanded": open,
                className: "inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-muted hover:text-foreground",
                onClick: ()=>setOpen((o)=>!o),
                type: "button",
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        "aria-hidden": "true",
                        className: unmatched.length > 0 ? 'size-3.5 text-[color:var(--warning)]' : 'size-3.5'
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: "How I got this"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "aria-hidden": "true",
                        children: "·"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: summary
                    }),
                    /*#__PURE__*/ _jsx(ChevronDown, {
                        "aria-hidden": "true",
                        className: open ? 'size-3.5 rotate-180' : 'size-3.5'
                    })
                ]
            }),
            open && /*#__PURE__*/ _jsxs("div", {
                className: "mt-1.5 space-y-2 border-l border-border pl-3",
                children: [
                    tools.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "font-medium text-foreground",
                                children: "What I read"
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: "mt-0.5 space-y-0.5",
                                children: tools.map((t)=>/*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            toolName(t.name),
                                            t.calls > 1 ? ` (${t.calls}×)` : '',
                                            t.failed ? ` · ${t.failed} didn't return` : ''
                                        ]
                                    }, t.name))
                            })
                        ]
                    }),
                    figures.checked > 0 && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "font-medium text-foreground",
                                children: "Figures"
                            }),
                            unmatched.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                children: "Every figure in this answer matches something I read, or is worked out from those."
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        children: "These weren't found in anything I read. Treat them as estimates and check them before you rely on them:"
                                    }),
                                    /*#__PURE__*/ _jsx("ul", {
                                        className: "mt-0.5 flex flex-wrap gap-1.5",
                                        children: unmatched.map((f)=>/*#__PURE__*/ _jsx("li", {
                                                className: "rounded border border-border px-1.5 tabular-nums text-foreground",
                                                children: f.text
                                            }, f.text))
                                    })
                                ]
                            })
                        ]
                    }),
                    provenance.rechecked && /*#__PURE__*/ _jsx("p", {
                        children: "My first draft had figures I couldn't trace, so I checked and rewrote it once."
                    }),
                    failed > 0 && /*#__PURE__*/ _jsxs("p", {
                        children: [
                            failed,
                            " lookup",
                            failed === 1 ? '' : 's',
                            " didn't return anything I could use."
                        ]
                    })
                ]
            })
        ]
    });
}
